import React, { useState, useEffect } from 'react';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 40) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className={`site-header sticky-top bg-white ${isScrolled ? 'shadow-sm py-2' : 'py-2 py-lg-3'} transition-all`}>
      <div className="container">
        {/* Main Navbar Top Row: Always 1 single row, never wraps on mobile or desktop */}
        <div className="d-flex align-items-center justify-content-between w-100 flex-nowrap">
          {/* Brand Logo & Name */}
          <a
            href="#home"
            className="navbar-brand d-flex align-items-center gap-2 m-0 text-decoration-none min-w-0"
            style={{ maxWidth: 'calc(100% - 95px)' }}
          >
            {/* Official Logo Preserved Intact */}
            <div className="logo-wrapper flex-shrink-0">
              <img
                src="/logo192.png"
                alt="A.B. Engineering Works Official Logo"
                className="site-logo"
                width="44"
                height="44"
              />
            </div>
            <div className="brand-text min-w-0">
              <div className="fw-bold text-dark lh-1 brand-title text-truncate">A.B. Engineering Works</div>
              <span className="text-muted brand-subtitle d-block text-truncate">Pharmaceutical Machinery Spares</span>
            </div>
          </a>

          {/* Desktop Navigation Links & CTA (hidden on mobile, shown on lg+) */}
          <div className="d-none d-lg-flex align-items-center gap-1">
            <ul className="navbar-nav d-flex flex-row align-items-center gap-1 mb-0">
              <li className="nav-item">
                <a href="#home" className="nav-link px-3 fw-medium text-dark" onClick={closeMenu}>Home</a>
              </li>
              <li className="nav-item">
                <a href="#about" className="nav-link px-3 fw-medium text-dark" onClick={closeMenu}>About Us</a>
              </li>
              <li className="nav-item">
                <a href="#products" className="nav-link px-3 fw-medium text-dark" onClick={closeMenu}>Machinery Spares</a>
              </li>
              <li className="nav-item">
                <a href="#expertise" className="nav-link px-3 fw-medium text-dark" onClick={closeMenu}>Expertise</a>
              </li>
              <li className="nav-item">
                <a href="#why-us" className="nav-link px-3 fw-medium text-dark" onClick={closeMenu}>Why Us</a>
              </li>
              <li className="nav-item">
                <a href="#location" className="nav-link px-3 fw-medium text-dark" onClick={closeMenu}>Location</a>
              </li>
            </ul>
            <a
              href="#contact"
              className="btn btn-primary px-3.5 py-2 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm rounded-2 ms-2"
              onClick={closeMenu}
            >
              <i className="fas fa-paper-plane small"></i>
              <span>Get a Quote</span>
            </a>
          </div>

          {/* Mobile Right Actions: Call Button & Hamburger Toggle (Mobile only, strictly on same line) */}
          <div className="d-flex align-items-center gap-1.5 gap-sm-2 d-lg-none flex-shrink-0">
            <a
              href="tel:+918108765006"
              className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 px-2.5 py-1 rounded-pill header-call-btn"
              title="Call Workshop"
            >
              <i className="fas fa-phone-alt text-primary" style={{ fontSize: '0.72rem' }}></i>
              <span className="fw-semibold" style={{ fontSize: '0.78rem' }}>Call</span>
            </a>
            <button
              className="btn btn-sm btn-light border-0 p-1 ms-1 d-flex align-items-center justify-content-center mobile-toggle-btn focus-ring-none"
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle navigation"
              style={{ width: '36px', height: '36px', borderRadius: '8px' }}
            >
              <i className={`fas ${isMenuOpen ? 'fa-times fs-5 text-dark' : 'fa-bars fs-5 text-dark'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer (Dropdown under the header bar when opened) */}
        {isMenuOpen && (
          <div className="mobile-nav-drawer d-lg-none mt-2 pt-2 pb-3 border-top">
            <ul className="list-unstyled d-flex flex-column gap-1 mb-0">
              <li>
                <a href="#home" className="mobile-nav-link d-flex align-items-center px-3 py-2 text-dark text-decoration-none rounded-2 fw-medium" onClick={closeMenu}>
                  <i className="fas fa-home me-2.5 text-primary opacity-75" style={{ width: '18px' }}></i>
                  <span>Home</span>
                </a>
              </li>
              <li>
                <a href="#about" className="mobile-nav-link d-flex align-items-center px-3 py-2 text-dark text-decoration-none rounded-2 fw-medium" onClick={closeMenu}>
                  <i className="fas fa-industry me-2.5 text-primary opacity-75" style={{ width: '18px' }}></i>
                  <span>About Us</span>
                </a>
              </li>
              <li>
                <a href="#products" className="mobile-nav-link d-flex align-items-center px-3 py-2 text-dark text-decoration-none rounded-2 fw-medium" onClick={closeMenu}>
                  <i className="fas fa-boxes me-2.5 text-primary opacity-75" style={{ width: '18px' }}></i>
                  <span>Machinery Spares</span>
                </a>
              </li>
              <li>
                <a href="#expertise" className="mobile-nav-link d-flex align-items-center px-3 py-2 text-dark text-decoration-none rounded-2 fw-medium" onClick={closeMenu}>
                  <i className="fas fa-cogs me-2.5 text-primary opacity-75" style={{ width: '18px' }}></i>
                  <span>Expertise</span>
                </a>
              </li>
              <li>
                <a href="#why-us" className="mobile-nav-link d-flex align-items-center px-3 py-2 text-dark text-decoration-none rounded-2 fw-medium" onClick={closeMenu}>
                  <i className="fas fa-shield-alt me-2.5 text-primary opacity-75" style={{ width: '18px' }}></i>
                  <span>Why Us &amp; MSME</span>
                </a>
              </li>
              <li>
                <a href="#location" className="mobile-nav-link d-flex align-items-center px-3 py-2 text-dark text-decoration-none rounded-2 fw-medium" onClick={closeMenu}>
                  <i className="fas fa-map-marker-alt me-2.5 text-primary opacity-75" style={{ width: '18px' }}></i>
                  <span>Location</span>
                </a>
              </li>
              <li className="pt-2 mt-1 border-top">
                <a
                  href="#contact"
                  className="btn btn-primary w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2 rounded-2 shadow-sm"
                  onClick={closeMenu}
                >
                  <i className="fas fa-paper-plane small"></i>
                  <span>Request a Free Quote</span>
                </a>
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
